import { fallback } from "@tanstack/zod-adapter"
import { z } from "zod"

import { FREIGHT_QUOTE_NUMBER_MAX_LENGTH } from "./constants"
import { isCpoDeliveryDateLineTouched } from "./cpoDeliveryDateLine"

import { DateSpecialValue } from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import {
  ComputedProductStatus,
  CustomerOrderDateRangeType,
  CustomerOrderSortField,
  CustomerPurchaseOrderDateRangeType,
  CustomerPurchaseOrderStatus,
  FreightTerms,
  SalesOrderDateRangeType,
  SalesOrderStatus,
  ShipStatus,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { isAfterDate } from "@/utils/date"

export const customerOrderSearchSchema = z.object({
  query: z.string().optional(),
  field: fallback(z.nativeEnum(CustomerOrderSortField), CustomerOrderSortField.Customer),
  order: fallback(z.nativeEnum(SortingOrder), SortingOrder.Asc),
  statuses: fallback(z.array(z.nativeEnum(SalesOrderStatus)).optional(), undefined),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  dateRangeType: fallback(z.nativeEnum(CustomerOrderDateRangeType).optional(), undefined),
})

export type CustomerOrderSearchParams = z.infer<typeof customerOrderSearchSchema>

export const customerPurchaseOrderSearchSchema = z.object({
  query: fallback(z.string().optional().default(""), ""),
  statuses: fallback(z.array(z.nativeEnum(CustomerPurchaseOrderStatus)).optional(), undefined),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  dateRangeType: fallback(z.nativeEnum(CustomerPurchaseOrderDateRangeType).optional(), undefined),
})

export type CustomerPurchaseOrderSearchParams = z.infer<typeof customerPurchaseOrderSearchSchema>

export const salesOrderSearchSchema = z.object({
  query: z.string().optional(),
  statuses: fallback(z.array(z.nativeEnum(SalesOrderStatus)), [SalesOrderStatus.Open]),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  dateRangeType: fallback(z.nativeEnum(SalesOrderDateRangeType).optional(), undefined),
})

export type SalesOrderSearchParams = z.infer<typeof salesOrderSearchSchema>

export const customerPurchaseOrderFormSchema = z.object({
  cpoNumber: z.string().trim().min(1, "CPO # is required").max(50, "CPO # must not exceed 50 characters"), // AS400 constraint
})

export type CustomerPurchaseOrderFormValues = z.infer<typeof customerPurchaseOrderFormSchema>

export const deliveryDateLinePriceOverrideFormSchema = z.object({
  pricePerUom: z.number().positive("Price per UoM must be a positive number").nullable(),
})

export type DeliveryDateLinePriceOverrideFormValues = z.infer<typeof deliveryDateLinePriceOverrideFormSchema>

const FREIGHT_COST_MAX_DECIMALS = 2
// Largest value the BE numeric(12,2) freight cost column holds.
const FREIGHT_COST_MAX_VALUE = 9999999999.99

export const freightLinePriceFormSchema = z.object({
  pricePerUom: z
    .number()
    .min(0, "Freight cost cannot be negative")
    .max(FREIGHT_COST_MAX_VALUE, `Freight cost must be less than or equal to ${FREIGHT_COST_MAX_VALUE}`)
    .refine(
      (value) => Number.isInteger(Number((value * 10 ** FREIGHT_COST_MAX_DECIMALS).toFixed(6))),
      "Freight cost must have at most 2 decimal places"
    )
    .nullable(),
  priceUomId: z.string().nullable(),
})

export type FreightLinePriceFormValues = z.infer<typeof freightLinePriceFormSchema>

export const salesOrderFreightLineFormSchema = z.object({
  quantity: z
    .number()
    .nullable()
    .refine((value) => value !== null, "Quantity is required"),
  pricePerUom: freightLinePriceFormSchema.shape.pricePerUom,
  priceUomId: z.string().nullable(),
  quoteNumber: z.string().max(FREIGHT_QUOTE_NUMBER_MAX_LENGTH, "Quote number must not exceed 20 characters"),
})

export type SalesOrderFreightLineFormValues = z.infer<typeof salesOrderFreightLineFormSchema>

export const cpoDeliveryDateLineFormSchema = z
  .object({
    deliveryDateLineId: z.string(),
    customerPurchaseOrderProductLineId: z.string().nullable(),
    cpoLineItemId: z.string(),
    salesOrderId: z.string().nullable(),
    soNumber: z.string().nullable(),
    soStatus: z.nativeEnum(SalesOrderStatus).nullable(),
    shipFromLocationId: z.string().nullable(),
    defaultShipFromLocationId: z.string().nullable(),
    // May carry the DateSpecialValue.ASAP sentinel — the BE stores ship ASAP as an empty date plus a flag.
    shipByDate: z.string().nullable(),
    deliverByDate: z.string().nullable(),
    isDeliverAsap: z.boolean(),
    soPallets: z.number().positive("SO Pallets must be a positive number").nullable(),
    soUnits: z.number().positive("SO Units must be a positive number").nullable(),
    quantity: z.number().positive("Quantity must be a positive number").nullable(),
    soUom: z.string().nullable(),
    defaultSoUom: z.string().nullable(),
    pricePerUom: z.number().positive("Price per UoM must be a positive number").nullable(),
    isManualPriceSet: z.boolean(),
    createdAt: z.string(),
  })
  .refine((line) => !isCpoDeliveryDateLineTouched(line) || !!line.shipFromLocationId, {
    message: "Ship From is required",
    path: ["shipFromLocationId"],
  })
  .refine((line) => !isCpoDeliveryDateLineTouched(line) || !!line.shipByDate, {
    message: "Ship by Date is required",
    path: ["shipByDate"],
  })
  .refine((line) => !isCpoDeliveryDateLineTouched(line) || line.soPallets !== null, {
    message: "SO Pallets is required",
    path: ["soPallets"],
  })
  .refine((line) => !isCpoDeliveryDateLineTouched(line) || line.soUnits !== null, {
    message: "SO Units is required",
    path: ["soUnits"],
  })
  .refine((line) => !isCpoDeliveryDateLineTouched(line) || line.quantity !== null, {
    message: "Quantity is required",
    path: ["quantity"],
  })
  .refine((line) => !isCpoDeliveryDateLineTouched(line) || line.soUom !== null, {
    message: "SO UoM is required",
    path: ["soUom"],
  })

export type CpoDeliveryDateLineFormValues = z.infer<typeof cpoDeliveryDateLineFormSchema>

export const cpoLineItemFormSchema = z.object({
  cpoLineItemId: z.string(),
  customerPurchaseOrderProductId: z.string().nullable(),
  cpoId: z.string(),
  productId: z.number(),
  publicId: z.string().nullable(),
  externalSystemId: z.string().nullable(),
  customerCode: z.string().nullable(),
  customerDescription: z.string().nullable(),
  companyProductDescription: z.string().nullable(),
  supplierName: z.string().nullable(),
  computedProductStatus: z.nativeEnum(ComputedProductStatus).nullable(),
  pallets: z.number().nullable(),
  units: z.number().nullable(),
  uom: z.string().nullable(),
  quantity: z.number().nullable(),
  packageUsWeightUom: z.string().nullable(),
  packageMetricWeightUom: z.string().nullable(),
  packageUsWeight: z.number().nullable(),
  packageMetricWeight: z.number().nullable(),
  packagesPerPallet: z.number().nullable(),
  noteCount: z.number(),
  deliveryDateLines: z.array(cpoDeliveryDateLineFormSchema),
  createdAt: z.string(),
})

export type CpoLineItemFormValues = z.infer<typeof cpoLineItemFormSchema>

export const customerPurchaseOrderLineItemsFormSchema = z.object({
  lineItems: z.array(cpoLineItemFormSchema),
})

export type CustomerPurchaseOrderLineItemsFormValues = z.infer<typeof customerPurchaseOrderLineItemsFormSchema>

export const salesOrderLineFormSchema = z.object({
  lineId: z.string(),
  deliverDate: z.string().nullable(),
  pallets: z
    .number()
    .positive("Pallets must be a positive number")
    .nullable()
    .refine((value) => value !== null, "Pallets is required"),
  units: z
    .number()
    .positive("Units must be a positive number")
    .nullable()
    .refine((value) => value !== null, "Units is required"),
  quantityInUom: z
    .number()
    .positive("Quantity must be a positive number")
    .nullable()
    .refine((value) => value !== null, "Quantity is required"),
  uomId: z.string().nullable(),
  pricePerUom: z.number().nullable(),
  priceUomId: z.string().nullable(),
  isManualPriceSet: z.boolean().nullable(),
})

export type SalesOrderLineFormValues = z.infer<typeof salesOrderLineFormSchema>

const salesOrderLogisticsBaseFormSchema = z.object({
  shipFromWarehouseAreaId: z.string().nullable(),
  // Bulk header edit — resubmits every line with this date so the backend's regroup engine can
  deliveryDate: z
    .string()
    .nullable()
    .refine((value) => !!value, "Requested Delivery Date is required"),
  isDeliveryDateAsap: z.boolean(),
  ladingBill: z.string(),
  confirmationSentOn: z.string().nullable(),
  promisedShipOn: z.string().nullable(),
  requestedShipOn: z.string().nullable(),
  warehouseShippedOn: z.string().nullable(),
  incoterms: z.string().nullable(),
  incotermsLocationId: z.string().nullable(),
  shipViaCompanyId: z.string().nullable(),
  freightTerms: z.nativeEnum(FreightTerms).nullable(),
  trackingNumber: z.string(),
  shipmentStatus: z.nativeEnum(ShipStatus).nullable(),
  bolNotes: z.string(),
  pickTicketNotes: z.string(),
  freightLine: salesOrderFreightLineFormSchema.nullable(),
  lines: z.array(salesOrderLineFormSchema),
})

const validateRequestedDates = (
  data: z.infer<typeof salesOrderLogisticsBaseFormSchema>,
  ctx: z.RefinementCtx
): void => {
  const { requestedShipOn, deliveryDate } = data

  if (requestedShipOn === DateSpecialValue.ASAP || !requestedShipOn || !deliveryDate) return

  if (isAfterDate(requestedShipOn, deliveryDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Requested delivery date cannot be earlier than requested ship date.",
      path: ["deliveryDate"],
    })
  }
}

export const salesOrderLogisticsFormSchema = salesOrderLogisticsBaseFormSchema.superRefine(validateRequestedDates)

export type SalesOrderLogisticsFormValues = z.infer<typeof salesOrderLogisticsFormSchema>

export const salesOrderStatusFormSchema = z.object({
  status: z.nativeEnum(SalesOrderStatus),
})

export type SalesOrderStatusFormValues = z.infer<typeof salesOrderStatusFormSchema>
